import { useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import FlexPage from "components/layout/FlexPage";
import Breadcrumbs from "components/common/Breadcrumbs";
import Loading from "components/common/Loading";
import useTitle from "components/hooks/useTitle";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import { useUpdateProductPrompts } from "services/nux";
import { useDeleteScheduledAction } from "services/member";
import { LEARNING_STEPS } from "components/member/learningSteps";
import LearningStepBanner from "components/member/LearningStepBanner";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "components/common/ConfirmationDialog";
import { MemberScheduledAction } from "protogen/member_service_pb";
import { FamilyStatus } from "protogen/advisors_service_pb";

const STEP = LEARNING_STEPS.find((s) => s.key === "dynamic-reminders")!;
const BANNER_DISMISS_KEY = `learningStep_${STEP.key}_dismissed`;

const NEW_ROUTINE_PREFILL =
  "Can you help me set up a routine? I'd like a weekly reminder to review my upcoming calendar and flag anything I should prepare for.";

const editPrefill = (action: MemberScheduledAction) => {
  const parts = [`I'd like to update my "${action.title}" routine`];
  if (action.scheduleSummary) parts.push(`(${action.scheduleSummary})`);
  parts.push(".");
  if (action.goal) parts.push(` It currently does: ${action.goal}.`);
  parts.push(" I want to change it to ");
  return parts.join("");
};

export default () => {
  useTitle("Routines");
  const navigate = useNavigate();
  const { data, refresh } = useMemberDetails();
  const { request: updateProductPrompts } = useUpdateProductPrompts();
  const { request: deleteScheduledAction, loading: deleteLoading } =
    useDeleteScheduledAction();
  const productPrompts = data?.member?.productPrompts
    ? JSON.parse(data.member.productPrompts)
    : {};
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const showBanner =
    !bannerDismissed && productPrompts[BANNER_DISMISS_KEY] !== "dismissed";
  const confirmState = useConfirmationDialog();

  const handleDismissBanner = async () => {
    setBannerDismissed(true);
    await updateProductPrompts({
      key: BANNER_DISMISS_KEY,
      value: "dismissed",
    });
  };

  const handleEdit = (action: MemberScheduledAction) => {
    navigate("/conversations/in-app", {
      state: { prefill: editPrefill(action) },
    });
  };

  const handleDelete = (action: MemberScheduledAction) => {
    confirmState.openDialog(async () => {
      confirmState.setLoading(true);
      await deleteScheduledAction({ agentCronRef: action.agentCronRef });
      refresh();
    });
  };

  const handleNewRoutine = () => {
    navigate("/conversations/in-app", {
      state: { prefill: NEW_ROUTINE_PREFILL },
    });
  };

  const actions = data?.scheduledActions ?? [];
  const isDigitalDeactivated =
    data?.family?.status === FamilyStatus.DIGITAL_DEACTIVATED ||
    data?.family?.status === FamilyStatus.DIGITAL_DEACTIVATED_TRIAL;

  return (
    <FlexPage leftAligned sx={{ width: "100%" }}>
      <Breadcrumbs
        breadcrumbs={[{ name: "Home", link: "/home" }, { name: "Routines" }]}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "8px",
          width: "100%",
          maxWidth: "800px",
        }}
      >
        <Typography variant="display">Routines</Typography>
        {isDigitalDeactivated ? (
          <Button
            variant="contained"
            onClick={() => navigate("/digital-upgrade")}
          >
            Upgrade plan
          </Button>
        ) : (
          actions.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleNewRoutine}
            >
              New routine
            </Button>
          )
        )}
      </Box>
      {showBanner && (
        <LearningStepBanner step={STEP} onDismiss={handleDismissBanner} />
      )}
      {!data ? (
        <Loading />
      ) : actions.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Typography variant="body" color="text.secondary">
            You don't have any scheduled routines yet. Ask your agent to set up
            recurring reminders, follow-ups, or check-ins.
          </Typography>
          <Button
            variant="contained"
            onClick={() =>
              isDigitalDeactivated
                ? navigate("/digital-upgrade")
                : handleNewRoutine()
            }
          >
            {isDigitalDeactivated
              ? "Upgrade to resume routines"
              : "Set up your first routine"}
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            width: "100%",
            maxWidth: "800px",
          }}
        >
          {actions.map((action, index) => (
            <Box
              key={action.agentCronRef || index}
              sx={(theme) => ({
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "12px",
                padding: "16px 24px",
                borderRadius: "12px",
                border: `1px solid ${theme.palette.gray[4]}`,
              })}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body">{action.title}</Typography>
                <Typography
                  variant="bodySmall"
                  color="text.secondary"
                  display="block"
                >
                  {action.isPaused ? "Paused" : action.scheduleSummary}
                </Typography>
              </Box>
              {!action.isPaused && (
                <Box sx={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(action)}
                    aria-label="Edit routine"
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(action)}
                    aria-label="Delete routine"
                    disabled={deleteLoading}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
      <ConfirmationDialog
        {...confirmState.dialogProps}
        title="Delete routine"
        content="Are you sure you want to delete this routine? It will stop running immediately."
        yesButtonText="Delete"
        noButtonText="Cancel"
      />
    </FlexPage>
  );
};
